import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-tiktok-products',
  templateUrl: './tiktok-products.component.html',
  styleUrls: ['./tiktok-products.component.css']
})
export class TikTokProductsComponent implements OnInit {
  products: any[] = [];
  expandedProductId: string | null = null;
  isLoading = false;
  searchKeyword = '';
  merchantId = '';
  merchants: any[] = [];
  private preselectedMerchantId = '';

  // Tooltip state
  tooltipText = '';
  tooltipX = 0;
  tooltipY = 0;
  showTooltip = false;

  pageSize = 100;
  currentPage = 1;
  totalProducts = 0;
  totalProductsOfMerchant = 0;

  // FlashSale batch status tracking
  productsCountByStatus = {
    allRunning: 0,       // Xanh  - đang chạy, không có lỗi
    partiallyRunning: 0, // Vàng  - có batch active nhưng cũng có batch thất bại
    notRunning: 0        // Xám   - không có batch active
  };

  // Active filter for status badges (null = no filter)
  flashSaleStatusFilter: string | null = null;

  // Color mapping for status badges
  colorMap = {
    'allRunning': 'bg-success',
    'partiallyRunning': 'bg-warning',
    'notRunning': 'bg-secondary'
  };

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.preselectedMerchantId = this.route.snapshot.queryParamMap.get('merchantId') || '';
    this.loadMerchants();
  }

  loadMerchants() {
    this.http.get<any>('/api/Merchant/GetGrantedMerchants').subscribe(
      response => {
        this.merchants = Array.isArray(response) ? response : (response.data || []);
        // Filter for TikTok merchants
        this.merchants = this.merchants.filter(m =>
          m.tikTokAccessToken != null && m.tikTokAccessToken != ''
        );

        if (this.preselectedMerchantId) {
          const exists = this.merchants.some(m => m.merchantId === this.preselectedMerchantId);
          if (exists) {
            this.merchantId = this.preselectedMerchantId;
            this.preselectedMerchantId = '';
            this.onMerchantChange();
          }
        }
      },
      error => {
        console.error('Error loading merchants:', error);
        this.merchants = [];
      }
    );
  }

  onMerchantChange() {
    this.currentPage = 1;
    this.expandedProductId = null;
    this.flashSaleStatusFilter = null;
    this.loadProducts();
  }

  toggleStatusFilter(status: string) {
    this.flashSaleStatusFilter = this.flashSaleStatusFilter === status ? null : status;
    this.currentPage = 1;
    this.expandedProductId = null;
    this.loadProducts();
  }

  loadProducts() {
    if (!this.merchantId) {
      this.products = [];
      this.totalProducts = 0;
      this.totalProductsOfMerchant = 0;
      return;
    }

    this.isLoading = true;
    const params: any = {
      merchantId: this.merchantId,
      page: this.currentPage.toString(),
      pageSize: this.pageSize.toString()
    };

    if (this.searchKeyword) {
      params.searchTerm = this.searchKeyword;
    }

    if (this.flashSaleStatusFilter) {
      params.flashSaleStatus = this.flashSaleStatusFilter;
    }

    this.http.get<any>('/api/TikTokAd/GetProducts', { params }).subscribe(
      response => {
        this.products = response.data || [];
        this.totalProducts = response.total || 0;
        this.expandedProductId = null;
        this.isLoading = false;

        if (this.flashSaleStatusFilter === 'khong-chay') {
          this.products.sort((a, b) => {
            const aFailed = a.hasFailedBatch ? 0 : a.isExpired ? 1 : 2;
            const bFailed = b.hasFailedBatch ? 0 : b.isExpired ? 1 : 2;
            return aFailed - bFailed;
          });
        }

        // Use server-side summary counts (calculated across ALL products for this merchant, not just current page)
        if (response.statusSummary) {
          this.productsCountByStatus = {
            allRunning:       response.statusSummary.allRunning       || 0,
            partiallyRunning: response.statusSummary.partiallyRunning || 0,
            notRunning:       response.statusSummary.notRunning       || 0
          };
          this.totalProductsOfMerchant = response.statusSummary.total || 0;
        } else {
          this.calculateProductStatusSummary();
          this.totalProductsOfMerchant =
            this.productsCountByStatus.allRunning
            + this.productsCountByStatus.partiallyRunning
            + this.productsCountByStatus.notRunning;
        }
      },
      error => {
        console.error('Error loading products:', error);
        this.isLoading = false;
      }
    );
  }

  searchProducts() {
    this.currentPage = 1;
    this.expandedProductId = null;
    this.loadProducts();
  }

  toggleProductDetails(product: any) {
    if (this.expandedProductId === product.id) {
      this.expandedProductId = null;
      return;
    }
    this.expandedProductId = product.id;
    if (!product.variations) {
      product.loadingVariations = true;
      this.http.get<any[]>(`/api/TikTokAd/GetProductVariations/${product.id}`).subscribe(
        variations => {
          product.variations = variations;
          product.loadingVariations = false;
        },
        error => {
          console.error('Error loading variations:', error);
          product.loadingVariations = false;
        }
      );
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.expandedProductId = null;
      this.loadProducts();
    }
  }

  nextPage() {
    if ((this.currentPage * this.pageSize) < this.totalProducts) {
      this.currentPage++;
      this.expandedProductId = null;
      this.loadProducts();
    }
  }

  getStatusBadgeClass(status: string) {
    if (!status) return 'bg-secondary';
    return status.toLowerCase() === 'active' ? 'bg-success' : 'bg-danger';
  }

  /**
   * Calculate product count summary for flashsale status display
   */
  calculateProductStatusSummary() {
    this.productsCountByStatus = {
      allRunning: this.products.filter(p => p.skuRunStatus?.allRunning).length,
      partiallyRunning: this.products.filter(p => p.skuRunStatus?.partiallyRunning).length,
      notRunning: this.products.filter(p => p.skuRunStatus?.notRunning).length
    };
  }

  /**
   * Get status label for a product based on its SKU run status
   */
  getStatusLabel(product: any): string {
    if (!product || !product.skuRunStatus) return 'khong-chay';
    if (product.skuRunStatus.allRunning)       return 'dang-chay';
    if (product.skuRunStatus.partiallyRunning) return 'chay-mot-phan';
    return 'khong-chay';
  }

  /**
   * Get badge CSS class for a status label
   */
  getBadgeClass(statusLabel: string): string {
    const map: Record<string, string> = {
      'dang-chay':     'bg-success',
      'chay-mot-phan': 'bg-warning',
      'khong-chay':    'bg-secondary'
    };
    return map[statusLabel] || 'bg-secondary';
  }

  /**
   * Get status icon for a product
   */
  getStatusIcon(statusLabel: string): string {
    switch (statusLabel) {
      case 'dang-chay':    return 'fa-check-circle';
      case 'chay-mot-phan': return 'fa-exclamation-circle';
      default:             return 'fa-circle';
    }
  }

  /**
   * Format SKU status text for display
   */
  getSkuStatusText(product: any): string {
    if (!product || !product.skuRunStatus) return '0/0';
    const { runningCount, notRunningCount } = product.skuRunStatus;
    const total = runningCount + notRunningCount;
    return `${runningCount}/${total}`;
  }

  /**
   * Get tooltip text for batch expiration
   */
  getExpirationTooltip(product: any): string {
    if (!product || !product.batchEndTime) return '';
    const endTime = new Date(product.batchEndTime);
    return `Batch ends: ${endTime.toLocaleString()}`;
  }

  /**
   * Determine if a product's batch has expired
   */
  isBatchExpired(product: any): boolean {
    return product?.isExpired === true;
  }

  /**
   * Determine if a product has a failed batch
   */
  hasFailedBatch(product: any): boolean {
    return product?.hasFailedBatch === true;
  }

  /**
   * Return variations sorted: failed batch first, then others
   */
  getSortedVariations(product: any): any[] {
    if (!product?.variations?.length) return [];
    return [...product.variations].sort((a, b) => {
      const aFailed = a.failureMessage ? 0 : 1;
      const bFailed = b.failureMessage ? 0 : 1;
      return aFailed - bFailed;
    });
  }

  getFlashSaleProgress(startTime: string | null, endTime: string | null): number {
    if (!startTime || !endTime) return 0;
    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();
    const now = Date.now();
    if (end <= start) return 100;
    const pct = ((now - start) / (end - start)) * 100;
    return Math.min(100, Math.max(0, pct));
  }

  onFailMsgEnter(event: MouseEvent, message: string) {
    this.tooltipText = message;
    this.tooltipX = event.clientX + 12;
    this.tooltipY = event.clientY + 12;
    this.showTooltip = true;
  }

  onFailMsgMove(event: MouseEvent) {
    this.tooltipX = event.clientX + 12;
    this.tooltipY = event.clientY + 12;
  }

  onFailMsgLeave() {
    this.showTooltip = false;
  }

  /**
   * Convert UTC time to Vietnam time (UTC+7) and format for display
   */
  convertToVNTime(utcTime: string | Date): string {
    if (!utcTime) return '-';

    try {
      const date = new Date(utcTime);
      // Convert to VN time (UTC+7)
      // Get UTC time and add 7 hours for VN timezone
      const utcMs = date.getTime();
      const vnMs = utcMs + (7 * 60 * 60 * 1000);
      const vnDate = new Date(vnMs);

      // Format: "Apr 05, 2:30 PM" or "Apr 05, 14:30"
      const month = vnDate.toLocaleString('en-US', { month: 'short' });
      const day = vnDate.getDate().toString().padStart(2, '0');
      const year = vnDate.getFullYear();
      const hours = vnDate.getHours().toString().padStart(2, '0');
      const minutes = vnDate.getMinutes().toString().padStart(2, '0');

      return `${month} ${day}, ${year} ${hours}:${minutes}`;
    } catch (e) {
      return '-';
    }
  }
}
